import { ContainerTurnoverDto } from '@billing/app/api'
import ReportStep from '@billing/app/models/report-step.enum'
import { useBillingServiceStore } from '@billing/AppProvider'
import { Box, CircularProgress } from '@mui/material'
import { useTranslate } from '@tolgee/react'
import {
  EmptyIndicator,
  TomTable,
  tomTableColumnFilterPresets,
  useDefaultMRTOptions,
  useTomTable,
} from '@tom-ui/ui'
import { useTablePreferencesConfig } from '@tom-ui/utils'
import dayjs from 'dayjs'
import { MRT_ColumnDef, MRT_Row } from 'material-react-table'
import { observer } from 'mobx-react-lite'
import {
  exportToCsv,
  IDataExportConfig,
} from 'modules/ui/src/theme/components/table/utils/xlsxUtils'
import { useEffect, useMemo } from 'react'
import { CTReportFilter } from '../components/CTReport/CTReportFilter'
import { ReportNavigationButtonContainer } from './ReportNavigationButtonContainer'

export const CTReportContainer = observer(() => {
  const { reportsUIStore, containerTurnoversUIStore } = useBillingServiceStore()
  const { t } = useTranslate()

  const dataExportConfig = useMemo<IDataExportConfig<ContainerTurnoverDto>>(
    () => ({
      containerNumber: { header: t('containerNumber', 'Container Number'), type: 'string' },
      isoCode: { header: t('isoCode', 'ISO Code'), type: 'string' },
      operator: { header: t('operator', 'Operator'), type: 'string' },
      grossWeight: { header: t('weight', 'Weight'), type: 'number' },
      entryDateTime: { header: t('entryDate', 'Entry Date'), type: 'date' },
      isEmpty: { header: t('status', 'Status'), type: 'string' },
      isDangerous: { header: t('dangerous', 'Dangerous'), type: 'boolean' },
      isDamaged: { header: t('damaged', 'Damaged'), type: 'boolean' },
    }),
    [t],
  )

  const dataExport = {
    title: t('containerTurnoverReport', 'Container Turnover Report'),
    enableDataExport: true,
    dataExportConfig,
  }

  const columns = useMemo<MRT_ColumnDef<ContainerTurnoverDto>[]>(
    () => [
      {
        id: 'containerNumber',
        header: t('containerNumber', 'Container Number'),
        accessorFn: row => row.containerNumber,
        size: 200,
        ...tomTableColumnFilterPresets.searchMandatory(),
      },
      {
        id: 'isoCode',
        header: t('isoCode', 'ISO Code'),
        accessorFn: row => row.isoCode,

        ...tomTableColumnFilterPresets.searchMandatory(),
      },
      {
        id: 'operator',
        header: t('operator', 'Operator'),
        accessorFn: row => row.operator,
        ...tomTableColumnFilterPresets.searchMandatory(),
      },
      {
        id: 'grossWeight',
        header: t('weight', 'Weight'),
        accessorFn: row => row.grossWeight,
        size: 120,
        Cell: ({ cell }) => `${cell.getValue<number>()?.toLocaleString() || 0} kg`,
        ...tomTableColumnFilterPresets.numericMandatory(),
      },
      {
        id: 'entryDateTime',
        header: t('entryDate', 'Entry Date'),
        accessorFn: row => row.entryDateTime,
        ...tomTableColumnFilterPresets.dateMandatory(),
        Cell: ({ cell }) => {
          const date = cell.getValue<string>()
          return date ? dayjs(date).format('L LT') : EmptyIndicator
        },
      },
      {
        id: 'isEmpty',
        header: t('status', 'Status'),
        accessorFn: row => (row.isEmpty ? t('empty', 'Empty') : t('full', 'Full')),
        size: 100,
        ...tomTableColumnFilterPresets.searchOptional(),
      },
      {
        id: 'isDangerous',
        header: t('dangerous', 'Dangerous'),
        accessorFn: row => row.isDangerous,
        size: 100,
        Cell: ({ row }: { row: MRT_Row<ContainerTurnoverDto> }) =>
          row.original.isDangerous ? `${t('yes', 'Yes')}` : t('no', 'No'),
        ...tomTableColumnFilterPresets.searchOptional(),
      },
      {
        id: 'isDamaged',
        header: t('damaged', 'Damaged'),
        accessorFn: row => row.isDamaged,
        size: 100,
        Cell: ({ cell }) => (cell.getValue<boolean>() ? t('yes', 'Yes') : t('no', 'No')),
        ...tomTableColumnFilterPresets.searchOptional(),
      },
      {
        id: 'containerDamages',
        header: t('damages', 'Damages'),
        accessorFn: row => row.containerDamages?.map(i => i.types).join(', '),
        Cell: ({ cell }) => cell.getValue<string>() || EmptyIndicator,
        ...tomTableColumnFilterPresets.searchOptional(),
      },
    ],
    [t],
  )

  const defaultOptions = useDefaultMRTOptions<ContainerTurnoverDto>(undefined, dataExport)

  const { savedPreferences, onPreferencesChange } = useTablePreferencesConfig('billing/ct-report')

  const table = useTomTable(
    {
      columns,
      data: containerTurnoversUIStore.turnovers,
      enableRowSelection: false,
      enableTopToolbar: false,
      enableExpanding: false,
      enableRowActions: false,
      initialState: {
        ...defaultOptions.initialState,
        columnPinning: {
          left: [...(defaultOptions.initialState?.columnPinning?.left ?? []), 'containerNumber'],
        },
      },
      muiTableContainerProps: {
        sx: {
          minHeight: '450px',
        },
      },
    },
    undefined,
    dataExport,
    { columns, savedPreferences, onPreferencesChange },
  )

  useEffect(() => {
    if (reportsUIStore.currentStep === ReportStep.Report) {
      containerTurnoversUIStore.fetchData()
    }
  }, [containerTurnoversUIStore, reportsUIStore.currentStep])

  const finish = () => {
    containerTurnoversUIStore.resetData()
    reportsUIStore.resetStepper()
  }

  const handleDownload = () => {
    const rows = table.getPrePaginationRowModel().rows

    const dataToExport = rows.map(row => {
      const item = row.original
      return {
        ...item,
        isEmpty: item.isEmpty ? t('empty', 'Empty') : t('full', 'Full'),
        isDangerous: item.isDangerous ? t('yes', 'Yes') : t('no', 'No'),
        isDamaged: item.isDamaged ? t('yes', 'Yes') : t('no', 'No'),
      }
    })

    exportToCsv(
      t('containerTurnoverReport', 'Container Turnover Report'),
      dataToExport,
      dataExportConfig,
      `Container_Turnover_${dayjs().format('YYYYMMDD')}.csv`,
    )
  }

  return (
    <>
      {reportsUIStore.currentStep === ReportStep.Filter && <CTReportFilter />}

      {reportsUIStore.currentStep === ReportStep.Report && (
        <Box sx={{ width: '100%', mt: 1 }}>
          {containerTurnoversUIStore.isLoading ? <CircularProgress /> : <TomTable table={table} />}
        </Box>
      )}

      <ReportNavigationButtonContainer
        downloadAction={() => {
          handleDownload()
        }}
        finishAction={finish}
      />
    </>
  )
})
